import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import {
  ChartLineUp, CreditCard, Users, CurrencyCircleDollar,
  ArrowRight, CaretLeft, CaretRight
} from '@phosphor-icons/react';

const SLIDES = [
  {
    id: 'divispend',
    badge: 'DiviSpend',
    badgeColor: '#22C55E',
    heading: 'Let your fans invest in you',
    subtext: 'Enable DiviSpend on any listing and let supporters invest for a share of your sales. They earn when you earn — a partnership built into the platform.',
    cta: 'Start Selling',
    ctaLink: '/register',
    bgGradient: 'from-[#0D1F17] to-[#111111]',
    accentColor: '#22C55E',
    icon: ChartLineUp,
  },
  {
    id: 'payments',
    badge: 'Payment Options',
    badgeColor: '#22C55E',
    heading: 'Accept every way people pay',
    subtext: 'Card, Apple Pay, Cash App, PayPal, Venmo, and wallet-to-wallet transfers. Your customers choose how they pay — you just collect.',
    cta: 'Get Started',
    ctaLink: '/register',
    bgGradient: 'from-[#0D1F17] to-[#111111]',
    accentColor: '#22C55E',
    icon: CreditCard,
    pills: ['Card', 'Apple Pay', 'Cash App', 'PayPal', 'Venmo', 'Wallet'],
  },
  {
    id: 'follow',
    badge: 'Community',
    badgeColor: '#22C55E',
    heading: 'Follow your favorite providers',
    subtext: 'Discover sellers, follow the ones you love, and get their latest products in your personalized feed. Build relationships, not just transactions.',
    cta: 'Explore Now',
    ctaLink: '/explore',
    bgGradient: 'from-[#0D1F17] to-[#111111]',
    accentColor: '#22C55E',
    icon: Users,
  },
  {
    id: 'dividends',
    badge: 'Your Dividends',
    badgeColor: '#22C55E',
    heading: 'Get your dividends on Divispend',
    subtext: "We all dream of getting paid doing what we love. Whether it's cooking, designing, coaching, or creating — put it on Divispend and start earning from your craft.",
    cta: 'Sell Your Craft',
    ctaLink: '/register',
    bgGradient: 'from-[#0D1F17] to-[#111111]',
    accentColor: '#22C55E',
    icon: CurrencyCircleDollar,
  },
];

export default function HeroBanner({ isAuthenticated }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setCurrent(c => (c + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCurrent(c => (c - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [paused, next]);

  const slide = SLIDES[current];
  const SlideIcon = slide.icon;

  return (
    <section
      className="relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-banner"
    >
      <div className={`bg-gradient-to-br ${slide.bgGradient} transition-all duration-700`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-28">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Text side */}
            <div className="flex-1 text-center lg:text-left" key={slide.id}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6" style={{ backgroundColor: `${slide.badgeColor}15` }}>
                <SlideIcon className="w-4 h-4" style={{ color: slide.badgeColor }} weight="fill" />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: slide.badgeColor }}>{slide.badge}</span>
              </div>
              <h1
                className="text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter text-white leading-[1.1] max-w-2xl"
                style={{ fontFamily: 'Cabinet Grotesk' }}
                data-testid="hero-heading"
              >
                {slide.heading}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-[#999999] max-w-xl leading-relaxed">
                {slide.subtext}
              </p>

              {slide.pills && (
                <div className="mt-5 flex flex-wrap gap-2 justify-center lg:justify-start">
                  {slide.pills.map(p => (
                    <span key={p} className="inline-flex items-center bg-[#1A1A1A] border border-[#333333] px-3 py-1.5 rounded-full text-xs font-medium text-[#999999]">{p}</span>
                  ))}
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-4 justify-center lg:justify-start">
                <Link to={isAuthenticated ? "/dashboard" : slide.ctaLink}>
                  <Button className="text-white rounded-sm h-12 px-8 text-base font-semibold" style={{ backgroundColor: slide.accentColor }} data-testid="hero-cta-button">
                    {isAuthenticated ? "Go to Dashboard" : slide.cta} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/explore">
                  <Button variant="outline" className="rounded-sm h-12 px-8 text-base font-semibold border-[#333333] hover:bg-[#222222] text-white" data-testid="explore-cta-button">Explore marketplace</Button>
                </Link>
              </div>
            </div>

            {/* Visual side */}
            <div className="hidden lg:flex w-72 h-72 rounded-2xl items-center justify-center border" style={{ backgroundColor: `${slide.accentColor}08`, borderColor: `${slide.accentColor}20` }}>
              <SlideIcon className="w-24 h-24" style={{ color: slide.accentColor }} weight="duotone" />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4" data-testid="hero-nav">
        <button onClick={prev} className="text-[#666666] hover:text-white transition-colors p-1" data-testid="hero-prev" aria-label="Previous slide">
          <CaretLeft className="w-5 h-5" />
        </button>
        <div className="flex gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setCurrent(i)}
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{ backgroundColor: i === current ? slide.accentColor : '#333333', width: i === current ? '24px' : '8px' }}
              data-testid={`hero-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
        <button onClick={next} className="text-[#666666] hover:text-white transition-colors p-1" data-testid="hero-next" aria-label="Next slide">
          <CaretRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
}
